const MAX_INT_32 = 2147483647;

/**
 * Checks if a given value is within the range of a 32-bit signed integer.
 *
 * @param {number} value - The value to be checked.
 * @returns {boolean} True if the value is within the range of a 32-bit signed integer, false otherwise.
 */
export const isWithinMaxInt32 = (value) => {
  return (
    typeof value === "number" && value <= MAX_INT_32 && value >= -MAX_INT_32
  );
};

/**
 * Cleans and returns a double input value by keeping only valid double values up to 2 decimal places.
 *
 * @param {string} value - The input value to be cleaned.
 * @returns {string} The cleaned double value with up to 2 decimal places.
 */
export const useDoubleInput = (value) => {
  const regex = /^(?!.*\.\.)(?!^\.$)[0-9]*(\.[0-9]{0,2})?$/;

  value = value.replace(/^(\d+\.\d{1,2}).*/, "$1");
  value = value.replace(/^0+(?=\d)/, "");

  if (!regex.test(value)) {
    value = "0";
  }
  return value;
};

/**
 * Validates and parses a numeric input value.
 *
 * @param {string|number} value - The input value to validate and parse.
 * @param {number|null} min - Minimum allowed value.
 * @param {number|null} max - Maximum allowed value.
 * @param {number|null} length - Max number of digits allowed (ignoring commas).
 * @return {number|string} The parsed numeric value, or "" if the input is invalid/empty.
 */
export const useNumericInput = (
  value,
  min = null,
  max = null,
  length = null
) => {
  if (typeof value === "string") {
    value = value.replace(/,/g, "");

    if (length && typeof length === "number") {
      value = value.substring(0, length);
    }

    const regex = /^[0-9]*$/;
    if (!regex.test(value)) {
      value = value.replace(/[^0-9]/g, "");
    }
  }

  if (!value) return "";

  let int = parseInt(value);

  if (max && int > max) return max;
  if (min && int < min) return min;

  return int;
};

/**
 * Only allows numeric values eg: 1-9 and not alphabets and returns a string
 * @param value has to be a string
 */
export const isStrNumericInput = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  const regex = /^[0-9]*$/;

  if (!regex.test(value)) {
    value = value.replace(/[^0-9]/g, "");
  }

  return value;
};

/**
 * Cleans and returns a string input value if it is a non-empty string.
 *
 * @param {string} value - The input value to be cleaned.
 * @returns {string} The cleaned string input value.
 */
export const useStringInput = (value, length = null) => {
  if (typeof value === "string" && value.trim() !== "") {
    if (length && typeof length === "number") {
      value = value.substring(0, length);
    }
    return value;
  }

  return "";
};

/**
 * Checks if a given link is a valid URL.
 *
 * @param {string} link - The URL to be validated.
 * @return {boolean} True if the link is a valid URL, false otherwise.
 */
export const validURL = (link) => {
  const validURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  return typeof link === "string" && link && validURL(link);
};

export const upperFirst = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const useDecimalInput = (value, min = null, max = null) => {
  if (typeof value === "string") {
    value = value.replace(/,/g, "");
    value = value.replace(/[^0-9.]/g, "");

    const parts = value.split(".");

    if (parts[0].length > 8) {
      parts[0] = parts[0].substring(0, 8);
    }

    let formatted = parts[0];
    if (parts.length > 1) {
      formatted += "." + parts[1].substring(0, 2);
    }

    value = formatted;
  }

  if (value === "" || value === ".") return value;

  let num = parseFloat(value);
  if (isNaN(num)) return "";

  if (max !== null && num > max) return max;
  if (min !== null && num < min) return min;

  return value;
};

const EMAIL_REGEX =
  /^[a-zA-Z0-9_%+-](?:[a-zA-Z0-9_%+-]|\.(?!\.))*[a-zA-Z0-9_%+-]@(?:[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.)+[a-zA-Z]{2,}$/;

/**
 * Validates an email address against RFC 5321 rules.
 * Local part: 3–64 chars, letters/digits/. _ % + -, no leading/trailing dot, no consecutive dots.
 * Domain: labels can't start/end with hyphen, TLD letters-only min 2 chars, max 253 chars total.
 *
 * @param {string} value - The email address to validate.
 * @returns {boolean} True if the email is valid, false otherwise.
 */
export const isValidEmail = (value) => {
  if (!value) return false;
  const trimmed = value.trim();
  const atIndex = trimmed.indexOf("@");
  if (atIndex === -1) return false;
  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  if (local.length < 3 || local.length > 64) return false;
  if (domain.length > 253) return false;
  return EMAIL_REGEX.test(trimmed);
};
